/// <amd-module name="Reporting/BoardsReportView/CfdReportCard" />
import { BoardColumnsQuery } from "Analytics/Boards/CumulativeFlowDiagramQueries/BoardColumnsQuery";
import { CumulativeFlowHistoryQuery } from "Analytics/Boards/CumulativeFlowDiagramQueries/CumulativeFlowHistoryQuery";
import { CardMetricsVisual, ISecondaryMetric, MetricSize } from "Analytics/Shared/Visuals/CardMetricsVisual/CardMetricsVisual";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import * as React from "react";
import { getNavigateToBoardHandler } from "Reporting/BoardsCommon/BoardsRoutingHelper";
import { publishEmptyReportTelemetry, telemetryAreaName } from "Reporting/BoardsCommon/ReportTelemetry";
import * as Resources from "Reporting/BoardsReportView/Resources";
import { CFDReport } from "Reporting/CFDReport/CfdReport";
import { CFDReportUserSettings } from "Reporting/CFDReport/CfdReportUserSettings";
import { getBoard } from "Reporting/CFDReport/Pickers/BoardsQuery";
import * as CfdReportResources from "Reporting/CFDReport/Resources";
import { ICacheableQuery } from "Reporting/Common/ICacheableQuery";
import { ICancelablePromise } from "VSS/Core/Util/Promise";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { traceException } from "VSS/Platform/Trace";
import { addDays, fetchTodayInAccountTimeZone, parseIsoDateStringInLocalTime } from "WidgetCommon/DateUtils/DateUtils";
import { getAverageWip } from "Widgets/CumulativeFlowDiagram/CfdAggregationHelper";
import { reportCommand } from "Widgets/CumulativeFlowDiagram/QueryUtilities";
import { BoardsReportCardProps } from "./BoardsReportSelectorProps";
import { PlaceholderBoardUnsupportedError } from "./PlaceholderBoardUnsupportedError";

interface CfdReportCardState {
    dataReady: boolean;
    averageWip?: number;
    errorMessage?: string;
    unmaterializedBoard?: boolean;
}

/**
 * Retrieves average velocity data and renders a velocity card with a hero metric.
 */
export class CfdReportCard extends VssComponent<BoardsReportCardProps, CfdReportCardState> {
    private static readonly ReportTelemetryFeatureName: string = "CfdReportCard";

    private queryService: ICacheableQueryService;
    private metricsPromise: ICancelablePromise<CfdReportCardState>;

    constructor(props: BoardsReportCardProps, context?: IVssComponentContext) {
        super(props, context);
        this.queryService = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
        this.state = {
            averageWip: undefined,
            dataReady: false
        };
    }

    public async componentDidMount() {
        this.loadCfdMetricsData();
    }

    public async componentDidUpdate(prevProps: BoardsReportCardProps) {
        if (JSON.stringify(prevProps) !== JSON.stringify(this.props)) {
            this.loadCfdMetricsData();
        }
    }

    public render(): JSX.Element {
        let wipMetric = this.getCardMetric(Resources.WorkInProgressMetricLabel, this.state.averageWip);
        let errorText = undefined;
        let onCardClick = this.props.onCardClick;
        if (this.state.unmaterializedBoard) {
            errorText = format(Resources.Error_PlaceholderBoardUnsupported_Format, this.props.categoryName);
            wipMetric = {
                label: "",
                value: ""
            };
            onCardClick = getNavigateToBoardHandler(this.context.pageContext);
        } else if (this.state.errorMessage) {
            errorText = this.state.errorMessage;
        } else if (wipMetric === undefined && this.state.dataReady) {
            wipMetric = {
                label: "",
                value: ""
            };
        }

        return (
            <CardMetricsVisual
                title={Resources.CumulativeFlowDiagramReportName}
                dataValidationPromises={[]}
                primaryMetric={undefined}
                secondaryMetric={wipMetric}
                secondaryMetricSize={{
                    label: MetricSize.Medium,
                    value: MetricSize.XLarge
                }}
                insight=""
                onCardClick={onCardClick}
                errorText={errorText}
            />
        );
    }

    public async runQuery<T>(query: ICacheableQuery<T>): Promise<T> {
        return this.queryService.getCacheableQueryResult(query);
    }

    private async loadCfdMetricsData() {
        this.metricsPromise && this.metricsPromise.cancel();

        try {
            this.metricsPromise = this.trackPromise(this.getCfdMetrics());

            const metricsData = await this.metricsPromise.promise;

            this.setState(metricsData);
        } catch (error) {
            if (error.isCanceled) {
                return; // Do nothing if the promises were cancelled
            }
            traceException(this.context.pageContext, telemetryAreaName, CfdReportCard.ReportTelemetryFeatureName, error);
            let unmaterializedBoard: boolean | undefined = undefined;
            if (error instanceof PlaceholderBoardUnsupportedError) {
                unmaterializedBoard = true;
            }
            this.setState({
                dataReady: true,
                errorMessage: error.message,
                unmaterializedBoard: unmaterializedBoard
            });
        }
    }

    private getCardMetric(label: string, relevantMetric?: number): ISecondaryMetric | undefined {
        let metric: ISecondaryMetric | undefined = undefined;
        if (relevantMetric !== undefined) {
            metric = {
                label: label,
                value: relevantMetric.toString()
            };
        } else {
            // return undefined. this is expected behavior
        }
        return metric;
    }

    private async getCfdMetrics(): Promise<CfdReportCardState> {
        const board = await getBoard(this.context.pageContext, this.props.projectId, this.props.teamId, this.props.categoryReferenceName);

        if (board == undefined || board.BoardId == undefined) {
            publishEmptyReportTelemetry(this.context.pageContext, CfdReportCard.ReportTelemetryFeatureName, CfdReportResources.UnmaterializedBoard);
            throw new PlaceholderBoardUnsupportedError(this.props.projectId, { id: this.props.teamId, name: this.props.teamName }, board);
        }

        const columnsQuery = new BoardColumnsQuery(reportCommand, this.props.projectId, board.BoardId);
        const [userSettings, columnsData] = await Promise.all([
            CFDReportUserSettings.load(this.context.pageContext, this.props.teamId, this.props.categoryReferenceName),
            this.runQuery(columnsQuery)
        ]);
        const rollingDuration = userSettings.rollingDuration || CFDReport.defaultRollingDuration;
        const startDate = await this.calculateStartDate(rollingDuration);
        const historyQuery = new CumulativeFlowHistoryQuery(reportCommand, {
            project: this.props.projectId,
            boardId: board.BoardId,
            boardLane: userSettings.boardLane,
            startDate: startDate
        });
        const cfHistory = await this.runQuery(historyQuery);
        let boardColumnIds = userSettings.boardColumnIds;
        if (boardColumnIds === undefined) {
            boardColumnIds = columnsData.boardColumns.map(x => x.ColumnId); // If no settings are saved then the default is all columns.
        }
        const averageWip = getAverageWip(columnsData.boardColumns, cfHistory, boardColumnIds);

        return {
            averageWip,
            dataReady: true
        };
    }

    private async calculateStartDate(rollingDuration: number): Promise<string> {
        const today = await fetchTodayInAccountTimeZone(this.context.pageContext);
        const startDate = addDays(parseIsoDateStringInLocalTime(today), -1 * rollingDuration);
        return startDate.toISOString().substr(0, 10);
    }
}
